import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
  Toast,
  ToastContainer,
} from "react-bootstrap";

import {
  ArrowLeft,
  CheckCircleFill,
  CloudArrowUpFill,
  EyeFill,
  FileEarmarkPdfFill,
  Files,
  PencilSquare,
  PlusCircleFill,
  Search,
  TrashFill,
  XCircleFill,
} from "react-bootstrap-icons";

import "./AdminDocumentos.css";

/* ============================================================
   API
============================================================ */

const API =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   FORMULARIO INICIAL
============================================================ */

const formularioInicial = {
  nombre: "",
  orden: "",
  activo: true,
};

/* ============================================================
   COMPONENTE
============================================================ */

export default function AdminDocumentos() {
  const navigate = useNavigate();

  /* ============================================================
     ESTADOS
  ============================================================ */

  const [documentos, setDocumentos] = useState([]);

  const [form, setForm] = useState(formularioInicial);

  const [archivo, setArchivo] = useState(null);

  const [editando, setEditando] = useState(null);

  const [busqueda, setBusqueda] = useState("");

  const [cargando, setCargando] = useState(true);

  const [guardando, setGuardando] = useState(false);

  const [eliminando, setEliminando] = useState(false);

  const [documentoEliminar, setDocumentoEliminar] = useState(null);

  const [showDelete, setShowDelete] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    mensaje: "",
    bg: "success",
  });

  /* ============================================================
     TOKEN
  ============================================================ */

  const obtenerToken = () => {
    return sessionStorage.getItem("gj_admin_token");
  };

  /* ============================================================
     HEADERS
  ============================================================ */

  const authHeaders = () => ({
    Authorization: `Bearer ${obtenerToken()}`,
  });

  /* ============================================================
     MOSTRAR MENSAJE
  ============================================================ */

  const mostrarToast = (mensaje, bg = "success") => {
    setToast({
      show: true,
      mensaje,
      bg,
    });
  };

  /* ============================================================
     CARGAR DOCUMENTOS
  ============================================================ */

  const cargarDocumentos = async () => {
    try {
      setCargando(true);

      const token = obtenerToken();

      if (!token) {
        navigate("/login", {
          replace: true,
        });

        return;
      }

      const respuesta = await axios.get(`${API}/admin/documentos`, {
        headers: authHeaders(),
        timeout: 45000,
      });

      setDocumentos(Array.isArray(respuesta.data) ? respuesta.data : []);
    } catch (error) {
      console.error("Error cargando documentos:", error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        sessionStorage.removeItem("gj_admin_token");

        sessionStorage.removeItem("gj_admin_usuario");

        navigate("/login", {
          replace: true,
        });

        return;
      }

      mostrarToast(
        error.response?.data?.message ||
          "No se pudieron cargar los documentos.",
        "danger",
      );
    } finally {
      setCargando(false);
    }
  };

  /* ============================================================
     CARGA INICIAL
  ============================================================ */

  useEffect(() => {
    cargarDocumentos();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================================================
     DOCUMENTOS FILTRADOS
  ============================================================ */

  const documentosFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    if (!texto) {
      return documentos;
    }

    return documentos.filter((documento) =>
      documento.nombre?.toLowerCase().includes(texto),
    );
  }, [documentos, busqueda]);

  /* ============================================================
     TOTAL ACTIVOS
  ============================================================ */

  const totalActivos = useMemo(() => {
    return documentos.filter((documento) => documento.activo !== false).length;
  }, [documentos]);

  /* ============================================================
     CAMBIAR FORMULARIO
  ============================================================ */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((anterior) => ({
      ...anterior,

      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ============================================================
     SELECCIONAR PDF
  ============================================================ */

  const handleArchivo = (e) => {
    const seleccionado = e.target.files?.[0];

    if (!seleccionado) {
      setArchivo(null);
      return;
    }

    const esPdf =
      seleccionado.type === "application/pdf" ||
      seleccionado.name.toLowerCase().endsWith(".pdf");

    if (!esPdf) {
      e.target.value = "";

      setArchivo(null);

      mostrarToast("Solo puedes seleccionar archivos PDF.", "danger");

      return;
    }

    setArchivo(seleccionado);
  };

  /* ============================================================
     LIMPIAR FORMULARIO
  ============================================================ */

  const limpiarFormulario = () => {
    setForm(formularioInicial);

    setArchivo(null);

    setEditando(null);

    const input = document.getElementById("admin-documento-archivo");

    if (input) {
      input.value = "";
    }
  };

  /* ============================================================
     NUEVO DOCUMENTO
  ============================================================ */

  const nuevoDocumento = () => {
    limpiarFormulario();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ============================================================
     EDITAR DOCUMENTO
  ============================================================ */

  const editarDocumento = (documento) => {
    setEditando(documento);

    setForm({
      nombre: documento.nombre || "",

      orden: documento.orden !== undefined ? documento.orden : "",

      activo: documento.activo !== false,
    });

    /*
     * No se puede colocar un archivo remoto
     * dentro de un input type="file".
     *
     * Por eso dejamos archivo = null.
     * Si el usuario no selecciona otro PDF,
     * conservamos el actual.
     */

    setArchivo(null);

    const input = document.getElementById("admin-documento-archivo");

    if (input) {
      input.value = "";
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ============================================================
     GUARDAR DOCUMENTO
  ============================================================ */

  const guardarDocumento = async (e) => {
    e.preventDefault();

    if (!form.nombre.trim()) {
      mostrarToast("Ingresa el nombre del documento.", "warning");

      return;
    }

    /*
     * Solamente exigimos PDF cuando
     * estamos creando un documento.
     *
     * Cuando editamos puede conservar
     * el PDF que ya tiene.
     */

    if (!editando && !archivo) {
      mostrarToast("Selecciona el archivo PDF.", "warning");

      return;
    }

    try {
      setGuardando(true);

      const datos = new FormData();

      datos.append("nombre", form.nombre.trim());

      datos.append("orden", form.orden === "" ? "0" : form.orden);

      datos.append("activo", String(form.activo));

      /*
       * Solamente enviamos "archivo"
       * si se seleccionó uno.
       */

      if (archivo) {
        datos.append("archivo", archivo);
      }

      /* ==============================
         EDITAR
      ============================== */

      if (editando) {
        await axios.put(`${API}/admin/documentos/${editando._id}`, datos, {
          headers: {
            ...authHeaders(),
          },

          timeout: 60000,
        });

        mostrarToast("Documento actualizado correctamente.", "success");
      } else {
        /* ==============================
           CREAR
        ============================== */

        await axios.post(`${API}/admin/documentos`, datos, {
          headers: {
            ...authHeaders(),
          },

          timeout: 60000,
        });

        mostrarToast("Documento agregado correctamente.", "success");
      }

      limpiarFormulario();

      await cargarDocumentos();
    } catch (error) {
      console.error("Error guardando documento:", error);

      mostrarToast(
        error.response?.data?.message || "No se pudo guardar el documento.",
        "danger",
      );
    } finally {
      setGuardando(false);
    }
  };

  /* ============================================================
     CAMBIAR ESTADO
  ============================================================ */

  const cambiarEstado = async (documento) => {
    try {
      const datos = new FormData();

      datos.append("activo", String(documento.activo === false));

      await axios.put(`${API}/admin/documentos/${documento._id}`, datos, {
        headers: {
          ...authHeaders(),
        },

        timeout: 45000,
      });

      mostrarToast(
        documento.activo === false
          ? "Documento activado."
          : "Documento desactivado.",
        "success",
      );

      await cargarDocumentos();
    } catch (error) {
      console.error("Error cambiando estado:", error);

      mostrarToast(
        error.response?.data?.message || "No se pudo cambiar el estado.",
        "danger",
      );
    }
  };

  /* ============================================================
     ABRIR MODAL ELIMINAR
  ============================================================ */

  const confirmarEliminar = (documento) => {
    setDocumentoEliminar(documento);

    setShowDelete(true);
  };

  /* ============================================================
     ELIMINAR DOCUMENTO
  ============================================================ */

  const eliminarDocumento = async () => {
    if (!documentoEliminar) {
      return;
    }

    try {
      setEliminando(true);

      await axios.delete(`${API}/admin/documentos/${documentoEliminar._id}`, {
        headers: authHeaders(),

        timeout: 45000,
      });

      setShowDelete(false);

      setDocumentoEliminar(null);

      if (editando?._id === documentoEliminar._id) {
        limpiarFormulario();
      }

      mostrarToast("Documento eliminado correctamente.", "success");

      await cargarDocumentos();
    } catch (error) {
      console.error("Error eliminando documento:", error);

      mostrarToast(
        error.response?.data?.message || "No se pudo eliminar el documento.",
        "danger",
      );
    } finally {
      setEliminando(false);
    }
  };

  /* ============================================================
     URL DEL PDF
  ============================================================ */

  const obtenerUrlDocumento = (documento) => {
    if (!documento?.archivo) {
      return "#";
    }

    if (
      documento.archivo.startsWith("http://") ||
      documento.archivo.startsWith("https://")
    ) {
      return documento.archivo;
    }

    return `${API}${documento.archivo}`;
  };

  /* ============================================================
     NOMBRE DEL ARCHIVO
  ============================================================ */

  const obtenerNombreArchivo = (documento) => {
    if (!documento?.archivo) {
      return "Sin archivo";
    }

    try {
      const urlLimpia = documento.archivo.split("?")[0];

      const partes = urlLimpia.split("/");

      const nombre = partes[partes.length - 1];

      return decodeURIComponent(nombre) || "Documento PDF";
    } catch (error) {
      return "Documento PDF";
    }
  };

  /* ============================================================
     VISTA
  ============================================================ */

  return (
    <div className="admin-documentos-page">
      {/* ========================================================
          TOAST
      ======================================================== */}

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={toast.show}
          onClose={() =>
            setToast((anterior) => ({
              ...anterior,
              show: false,
            }))
          }
          delay={3500}
          autohide
          bg={toast.bg}
        >
          <Toast.Header>
            <strong className="me-auto">Documentos PDF</strong>
          </Toast.Header>

          <Toast.Body
            className={
              toast.bg === "danger" || toast.bg === "success"
                ? "text-white"
                : ""
            }
          >
            {toast.mensaje}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="admin-documentos-container">
        {/* ====================================================
            CABECERA
        ==================================================== */}

        <div className="admin-documentos-header">
          <div>
            <div className="admin-documentos-kicker">
              <FileEarmarkPdfFill />
              ADMINISTRACIÓN
            </div>

            <h1>Documentos PDF</h1>

            <p>
              Administra los manuales, catálogos y guías técnicas del Centro de
              Ayuda.
            </p>
          </div>

          <div className="admin-documentos-header-actions">
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/admin")}
            >
              <ArrowLeft className="me-2" />
              Volver
            </Button>

            <Button variant="primary" onClick={nuevoDocumento}>
              <PlusCircleFill className="me-2" />
              Nuevo documento
            </Button>
          </div>
        </div>

        {/* ====================================================
            RESUMEN
        ==================================================== */}

        <Row className="g-3 mb-4">
          <Col md={4}>
            <Card className="admin-documentos-stat">
              <Card.Body>
                <div className="admin-documentos-stat-icon">
                  <Files />
                </div>

                <div>
                  <span>Total documentos</span>

                  <strong>{documentos.length}</strong>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="admin-documentos-stat">
              <Card.Body>
                <div className="admin-documentos-stat-icon success">
                  <CheckCircleFill />
                </div>

                <div>
                  <span>Documentos activos</span>

                  <strong>{totalActivos}</strong>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="admin-documentos-stat">
              <Card.Body>
                <div className="admin-documentos-stat-icon danger">
                  <XCircleFill />
                </div>

                <div>
                  <span>Desactivados</span>

                  <strong>{documentos.length - totalActivos}</strong>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ====================================================
            FORMULARIO PARA AGREGAR / EDITAR
        ==================================================== */}

        <Card className="admin-documentos-form-card">
          <Card.Body>
            {/* CABECERA FORMULARIO */}

            <div className="admin-documentos-card-header">
              <div className="admin-documentos-card-icon">
                {editando ? <PencilSquare /> : <CloudArrowUpFill />}
              </div>

              <div>
                <span>
                  {editando ? "EDITANDO DOCUMENTO" : "NUEVO DOCUMENTO"}
                </span>

                <h2>
                  {editando
                    ? "Actualizar documento PDF"
                    : "Agregar documento PDF"}
                </h2>
              </div>
            </div>

            {/* FORMULARIO */}

            <Form onSubmit={guardarDocumento}>
              <Row className="g-3">
                {/* ========================
                    NOMBRE
                ======================== */}

                <Col lg={7}>
                  <Form.Group>
                    <Form.Label>Nombre del documento</Form.Label>

                    <Form.Control
                      type="text"
                      name="nombre"
                      value={form.nombre}
                      onChange={handleChange}
                      placeholder="Ej. Catálogo Copeland – Compresores"
                      disabled={guardando}
                    />
                  </Form.Group>
                </Col>

                {/* ========================
                    ORDEN
                ======================== */}

                <Col lg={3}>
                  <Form.Group>
                    <Form.Label>Orden</Form.Label>

                    <Form.Control
                      type="number"
                      name="orden"
                      value={form.orden}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      disabled={guardando}
                    />
                  </Form.Group>
                </Col>

                {/* ========================
                    ESTADO
                ======================== */}

                <Col lg={2}>
                  <Form.Group>
                    <Form.Label>Estado</Form.Label>

                    <div className="admin-documentos-switch">
                      <Form.Check
                        type="switch"
                        id="documento-activo"
                        name="activo"
                        checked={form.activo}
                        onChange={handleChange}
                        label={form.activo ? "Visible" : "Oculto"}
                        disabled={guardando}
                      />
                    </div>
                  </Form.Group>
                </Col>

                {/* ========================
                    PDF
                ======================== */}

                <Col lg={12}>
                  <Form.Group>
                    <Form.Label>Archivo PDF</Form.Label>

                    {/* ====================
                        PDF ACTUAL
                    ==================== */}

                    {editando?.archivo && (
                      <div className="admin-documentos-current-file mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <div className="admin-documentos-upload-icon">
                            <FileEarmarkPdfFill />
                          </div>

                          <div className="flex-grow-1">
                            <small className="d-block text-muted mb-1">
                              PDF ACTUAL
                            </small>

                            <strong className="d-block">
                              {obtenerNombreArchivo(editando)}
                            </strong>

                            <small className="text-muted">
                              Si no seleccionas otro PDF, este archivo se
                              conservará.
                            </small>
                          </div>

                          <a
                            href={obtenerUrlDocumento(editando)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm"
                          >
                            <EyeFill className="me-1" />
                            Ver PDF
                          </a>
                        </div>
                      </div>
                    )}

                    {/* ====================
                        SUBIR PDF
                    ==================== */}

                    <div className="admin-documentos-upload">
                      <div className="admin-documentos-upload-icon">
                        <CloudArrowUpFill />
                      </div>

                      <div className="admin-documentos-upload-content">
                        <strong>
                          {archivo
                            ? archivo.name
                            : editando
                              ? "Reemplazar PDF (opcional)"
                              : "Seleccionar archivo PDF"}
                        </strong>

                        <span>
                          {editando
                            ? "Selecciona otro archivo únicamente si deseas reemplazar el PDF actual."
                            : "Selecciona un archivo en formato PDF."}
                        </span>
                      </div>

                      <Form.Control
                        id="admin-documento-archivo"
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleArchivo}
                        disabled={guardando}
                      />
                    </div>

                    {/* NUEVO ARCHIVO */}

                    {editando && archivo && (
                      <div className="alert alert-warning py-2 mt-3 mb-0">
                        <strong>Se reemplazará el PDF actual.</strong>

                        <div className="small mt-1">
                          Nuevo archivo: {archivo.name}
                        </div>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* ========================
                  BOTONES FORMULARIO
              ======================== */}

              <div className="admin-documentos-form-actions">
                {editando && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={limpiarFormulario}
                    disabled={guardando}
                  >
                    Cancelar edición
                  </Button>
                )}

                <Button type="submit" variant="primary" disabled={guardando}>
                  {guardando ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Guardando...
                    </>
                  ) : editando ? (
                    <>
                      <CheckCircleFill className="me-2" />
                      Guardar cambios
                    </>
                  ) : (
                    <>
                      <CloudArrowUpFill className="me-2" />
                      Subir documento
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {/* ====================================================
            CABECERA DEL LISTADO
        ==================================================== */}

        <div className="admin-documentos-list-header">
          <div>
            <span>DOCUMENTOS</span>

            <h2>Manuales y catálogos</h2>
          </div>

          {/* BUSCADOR */}

          <div className="admin-documentos-search">
            <Search />

            <input
              type="text"
              placeholder="Buscar documento..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />

            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda("")}
                aria-label="Limpiar búsqueda"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* ====================================================
            CARGANDO / VACÍO / TABLA
        ==================================================== */}

        {cargando ? (
          /* ==============================
             CARGANDO
          ============================== */

          <div className="admin-documentos-loading">
            <Spinner animation="border" variant="primary" />

            <span>Cargando documentos...</span>
          </div>
        ) : documentosFiltrados.length === 0 ? (
          /* ==============================
             SIN DOCUMENTOS
          ============================== */

          <div className="admin-documentos-empty">
            <FileEarmarkPdfFill />

            <h3>No hay documentos</h3>

            <p>
              {busqueda
                ? "No encontramos documentos con esa búsqueda."
                : "Todavía no has agregado documentos PDF."}
            </p>
          </div>
        ) : (
          /* ==============================
             TABLA
          ============================== */

          <div className="admin-documentos-table-container">
            <Table
              responsive
              hover
              className="admin-documentos-table align-middle mb-0"
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: "65px",
                    }}
                  >
                    #
                  </th>

                  <th>Documento</th>

                  <th
                    style={{
                      width: "90px",
                    }}
                  >
                    Orden
                  </th>

                  <th
                    style={{
                      width: "110px",
                    }}
                  >
                    Estado
                  </th>

                  <th
                    className="text-center"
                    style={{
                      width: "330px",
                    }}
                  >
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody>
                {documentosFiltrados.map((documento, index) => (
                  <tr
                    key={documento._id}
                    className={
                      documento.activo === false
                        ? "admin-documento-row-inactive"
                        : ""
                    }
                  >
                    {/* NÚMERO */}

                    <td>
                      <span className="admin-documento-table-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </td>

                    {/* DOCUMENTO */}

                    <td>
                      <div className="admin-documento-table-info">
                        <div className="admin-documento-table-icon">
                          <FileEarmarkPdfFill />
                        </div>

                        <div className="admin-documento-table-text">
                          <strong className="admin-documento-table-title">
                            {documento.nombre}
                          </strong>

                          <small>{obtenerNombreArchivo(documento)}</small>
                        </div>
                      </div>
                    </td>

                    {/* ORDEN */}

                    <td>
                      <span className="admin-documento-order">
                        {documento.orden ?? 0}
                      </span>
                    </td>

                    {/* ESTADO */}

                    <td>
                      <Badge
                        bg={
                          documento.activo === false ? "secondary" : "success"
                        }
                        className="admin-documento-status"
                      >
                        {documento.activo === false ? "Oculto" : "Visible"}
                      </Badge>
                    </td>

                    {/* ACCIONES */}

                    <td>
                      <div className="admin-documento-table-actions">
                        {/* VER */}

                        <a
                          href={obtenerUrlDocumento(documento)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-sm"
                          title="Ver PDF"
                        >
                          <EyeFill />
                          <span>Ver</span>
                        </a>

                        {/* EDITAR */}

                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => editarDocumento(documento)}
                          title="Editar documento"
                        >
                          <PencilSquare />

                          <span>Editar</span>
                        </Button>

                        {/* ACTIVAR / OCULTAR */}

                        <Button
                          variant={
                            documento.activo === false
                              ? "outline-success"
                              : "outline-warning"
                          }
                          size="sm"
                          onClick={() => cambiarEstado(documento)}
                          title={
                            documento.activo === false
                              ? "Activar documento"
                              : "Ocultar documento"
                          }
                        >
                          {documento.activo === false ? (
                            <CheckCircleFill />
                          ) : (
                            <XCircleFill />
                          )}

                          <span>
                            {documento.activo === false ? "Activar" : "Ocultar"}
                          </span>
                        </Button>

                        {/* ELIMINAR */}

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => confirmarEliminar(documento)}
                          title="Eliminar documento"
                        >
                          <TrashFill />

                          <span>Eliminar</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* ========================================================
          MODAL ELIMINAR
      ======================================================== */}

      <Modal
        show={showDelete}
        onHide={() => {
          if (!eliminando) {
            setShowDelete(false);

            setDocumentoEliminar(null);
          }
        }}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar documento</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="admin-documentos-delete">
            <div className="admin-documentos-delete-icon">
              <TrashFill />
            </div>

            <h5>¿Eliminar este PDF?</h5>

            <p>Se eliminará:</p>

            <strong>{documentoEliminar?.nombre}</strong>

            <small>Esta acción no se puede deshacer.</small>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => {
              setShowDelete(false);

              setDocumentoEliminar(null);
            }}
            disabled={eliminando}
          >
            Cancelar
          </Button>

          <Button
            variant="danger"
            onClick={eliminarDocumento}
            disabled={eliminando}
          >
            {eliminando ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <TrashFill className="me-2" />
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
